import React from "react";
import { Link } from "react-router-dom";
import { siteConfig } from "../Datas/productData";
import "../css/delivery.scss";

function Delivery() {
  const {
    currencySymbol,
    freeShippingThreshold,
    shippingFlatRate,
    contact,
  } = siteConfig;
  const freeShippingLabel = `${currencySymbol}${freeShippingThreshold}`;
  const standardRateLabel = `${currencySymbol}${Number(shippingFlatRate).toFixed(2)}`;
  const shippingQuestions = [
    {
      question: "When will my order leave the studio?",
      answer: (
        <>
          <p>
            Orders are normally prepared within 1–2 business days. Orders
            placed after the daily cut-off, on weekends or on public holidays
            begin processing on the next business day.
          </p>
          <p>
            During collection launches and sale periods, processing can take a
            little longer. You will receive an email as soon as your parcel is
            handed to the carrier.
          </p>
        </>
      ),
    },
    {
      question: "How long does delivery take?",
      answer: (
        <>
          <p>
            Most European destinations arrive in 2–5 business days after
            dispatch. Deliveries outside Europe typically arrive in 4–9
            business days, depending on the destination and customs clearance.
          </p>
          <p>
            Delivery windows are estimates rather than guarantees. Your
            tracking page will always show the most recent carrier update.
          </p>
        </>
      ),
    },
    {
      question: "How much does shipping cost?",
      answer: (
        <p>
          Standard shipping is {standardRateLabel}. It is complimentary when
          the order subtotal reaches {freeShippingLabel}, after discounts and
          before any destination taxes. The final available service and cost
          are shown clearly at checkout.
        </p>
      ),
    },
    {
      question: "Can I track my parcel?",
      answer: (
        <p>
          Yes. When the order has shipped, we send a dispatch confirmation with
          a personal tracking link. It can take up to 24 hours for the first
          carrier scan to appear. If the link remains inactive after that,
          contact Client Services with your order number.
        </p>
      ),
    },
    {
      question: "Will I pay duties or import taxes?",
      answer: (
        <p>
          Depending on the destination, local authorities may collect duties,
          taxes or handling fees. Where these charges are not included at
          checkout, they are the recipient’s responsibility. Your checkout
          summary will indicate how taxes are handled before payment.
        </p>
      ),
    },
    {
      question: "Can I change my delivery address?",
      answer: (
        <p>
          We begin preparing orders quickly, so an address cannot be changed
          once the parcel has been packed. Contact us immediately after placing
          your order and we will confirm whether an update is still possible.
          For security, we cannot redirect a parcel to another country.
        </p>
      ),
    },
    {
      question: "What if my parcel arrives damaged or incomplete?",
      answer: (
        <p>
          Photograph the outer parcel, shipping label and item, then email us
          within 48 hours of delivery. Keep all packaging while we review the
          report with the carrier. Our team will explain the next step as soon
          as the delivery has been assessed.
        </p>
      ),
    },
  ];

  return (
    <main className="delivery" id="main-content" tabIndex="-1">
      <header className="delivery__header">
        <p>Client services / 01</p>
        <h1>Shipping</h1>
        <span>Fast, tracked delivery from our studio to your door.</span>
      </header>

      <section className="delivery__highlights" aria-label="Shipping highlights">
        <article>
          <span>01</span>
          <h2>Complimentary</h2>
          <p>Standard shipping from {freeShippingLabel}</p>
        </article>
        <article>
          <span>02</span>
          <h2>Tracked</h2>
          <p>A personal tracking link for every parcel</p>
        </article>
        <article>
          <span>03</span>
          <h2>Worldwide</h2>
          <p>Services selected for each destination</p>
        </article>
      </section>

      <section className="delivery__content" aria-labelledby="shipping-faq-title">
        <aside className="delivery__aside">
          <p>Need assistance?</p>
          <a href={`mailto:${contact.email}`}>{contact.email}</a>
          <span>{contact.hours}</span>
          <nav aria-label="Client service pages">
            <Link to="/returns">Returns &amp; exchanges</Link>
            <Link to="/contact">Contact us</Link>
          </nav>
        </aside>

        <div className="delivery__faq">
          <div className="delivery__faq-heading">
            <p>Everything you need to know</p>
            <h2 id="shipping-faq-title">Shipping questions</h2>
          </div>
          <div className="delivery__accordion">
            {shippingQuestions.map(({ question, answer }, index) => (
              <details key={question} open={index === 0}>
                <summary>
                  <span>{question}</span>
                  <span className="delivery__plus" aria-hidden="true" />
                </summary>
                <div className="delivery__answer">{answer}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="delivery__contact" aria-label="Contact client services">
        <div>
          <p>Still need help?</p>
          <h2>We are here for you.</h2>
        </div>
        <Link to="/contact">Contact client services</Link>
      </section>
    </main>
  );
}

export default Delivery;
